import { lazy, Suspense, useEffect, useState } from "react";
import { getCookieConsentValue } from "react-cookie-consent";
import { ErrorBoundary } from "react-error-boundary";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { QueryClient, QueryClientProvider } from "react-query";
import {
  BrowserRouter,
  Redirect,
  Route,
  Switch,
  useLocation,
} from "react-router-dom";
import ErrorState from "./shared/ErrorState";
import { Layout } from "./shared/Layout";

const AboutPage = lazy(() => import("./about/AboutPage"));
const HomePage = lazy(() => import("./home/HomePage"));
const PrivacyPolicy = lazy(() => import("./privacypolicy/PrivacyPolicy"));
const TermsAndConditions = lazy(() =>
  import("./termsandconditions/TermsAndConditions")
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
    },
  },
});

const GoogleAnalytics = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const cookieConsentValue = getCookieConsentValue() === "true";

  useEffect(() => {
    const loadGoogleAnalytics = async () => {
      const { default: ReactGA } = await import("react-ga");
      ReactGA.initialize("G-E1GD1SK4ND");
      setIsInitialized(true);
    };
    if (cookieConsentValue) {
      loadGoogleAnalytics();
    }
  }, [cookieConsentValue]);

  const location = useLocation();
  useEffect(() => {
    const pageview = async () => {
      const { default: ReactGA } = await import("react-ga");
      ReactGA.pageview(location.pathname + location.search);
    };
    if (isInitialized && cookieConsentValue) {
      pageview();
    }
  }, [cookieConsentValue, isInitialized, location]);

  return null;
};

export const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <Helmet
          defaultTitle="En plein milieu"
          titleTemplate="%s | En plein milieu"
        >
          <meta
            name="description"
            content="EnPleinMilieu.com te permet de déterminer l'endroit qui se trouve en plein milieu."
          />
        </Helmet>
        <BrowserRouter>
          <GoogleAnalytics />
          <Layout>
            <ErrorBoundary FallbackComponent={ErrorState}>
              <Suspense fallback={null}>
                <Switch>
                  <Route path="/" exact component={HomePage} />
                  <Route path="/a-propos" exact component={AboutPage} />
                  <Route
                    path="/politique-de-confidentialite"
                    exact
                    component={PrivacyPolicy}
                  />
                  <Route
                    path="/mentions-legales"
                    exact
                    component={TermsAndConditions}
                  />
                  <Route path="*">
                    <Redirect to="/" />
                  </Route>
                </Switch>
              </Suspense>
            </ErrorBoundary>
          </Layout>
        </BrowserRouter>
      </HelmetProvider>
    </QueryClientProvider>
  );
};
